import type { UserPreferences, PersistenceAPI } from '@atlaskit/editor-common/user-preferences';
import {
	UserPreferencesProvider,
	TOOLBAR_DOCKING_POSITIONS,
} from '@atlaskit/editor-common/user-preferences';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance';

import type {
	EditorPreferencesQuery_userPreferences_confluenceEditorSettings,
	EditorPreferencesQuery as EditorPreferencesQueryType,
} from './__types__/EditorPreferencesQuery';
import { EditorPreferencesQuery } from './EditorPreferencesQuery.graphql';
import type {
	EditorPreferencesMutation as EditorPreferencesMutationResponse,
	EditorPreferencesMutationVariables,
} from './__types__/EditorPreferencesMutation';
import { EditorPreferencesMutation } from './EditorPreferencesMutation.graphql';
// eslint-disable-next-line import/no-cycle
import { isSSR, LOCAL_STORAGE_KEY } from './EditorUserPreferencesProvider';
import { expVal } from '@confluence/feature-experiments';

const parseConfluenceEditorSettings = (
	confluenceEditorSettings:
		| EditorPreferencesQuery_userPreferences_confluenceEditorSettings
		| null
		| undefined,
) => {
	if (confluenceEditorSettings) {
		return Object.entries(confluenceEditorSettings).reduce<UserPreferences>((acc, [key, value]) => {
			if (
				key === 'toolbarDockingInitialPosition' &&
				TOOLBAR_DOCKING_POSITIONS.includes(value || '')
			) {
				return {
					...acc,
					toolbarDockingPosition: value,
				};
			}
			return {
				...acc,
				[key as keyof UserPreferences]: value,
			};
		}, {} as UserPreferences);
	}

	return {} as UserPreferences;
};

const updateLocalStorage = (userPreferences: UserPreferences) => {
	// confluenceLocalStorageInstance is only available in the browser environment
	if (!isSSR() && confluenceLocalStorageInstance) {
		confluenceLocalStorageInstance.setItem(LOCAL_STORAGE_KEY, JSON.stringify(userPreferences));
	}
};

export class UserPreferencesPersistenceAPI implements PersistenceAPI {
	private isLoggedin = true;
	private isInitialLoad = false;

	public constructor(isLoggedin = true) {
		this.setIsLoggedIn(isLoggedin);
	}

	public setIsLoggedIn(isLoggedin: boolean) {
		this.isLoggedin = isLoggedin;
	}

	public getInitialUserPreferences(): UserPreferences | undefined {
		let initialUserPreferences: UserPreferences | undefined;

		try {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const result = getApolloClient().readQuery<EditorPreferencesQueryType>({
				query: EditorPreferencesQuery,
			});

			if (result?.userPreferences) {
				initialUserPreferences = parseConfluenceEditorSettings(
					result.userPreferences?.confluenceEditorSettings,
				);
			}
		} catch (_error) {
			initialUserPreferences = undefined;
		}

		// If we don't have values from the Apollo cache, we try to fetch from local storage
		if (!initialUserPreferences && !isSSR()) {
			try {
				const userPreferenceStorage = confluenceLocalStorageInstance.getItem(LOCAL_STORAGE_KEY);
				if (userPreferenceStorage) {
					initialUserPreferences = JSON.parse(userPreferenceStorage);
				}
			} catch (_error) {
				// If the local storage is corrupted or not available, we ignore the error and return undefined
				initialUserPreferences = undefined;
			}
		}

		return initialUserPreferences;
	}

	public async updateUserPreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	) {
		if (!this.isLoggedin) {
			// If the user is not logged in, we return the key with updated value
			// This is to ensure that the editor can still function without flaws
			// but the value will not be persisted to the server
			return Promise.resolve({ [key]: value } as Pick<UserPreferences, K>);
		}

		// we only support updating the toolbarDockingInitialPosition preference ATM,
		// need to update this if we add more preferences
		const variables = {
			toolbarDockingInitialPosition: value,
		} as EditorPreferencesMutationVariables;

		try {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const { data } = await getApolloClient().mutate<
				EditorPreferencesMutationResponse,
				EditorPreferencesMutationVariables
			>({
				mutation: EditorPreferencesMutation,
				variables,
				update: (cache) => {
					// this is required to indicate toolbar changes to any useQuery for EditorPreferencesQuery, particularly progressive object header
					cache.writeQuery({
						query: EditorPreferencesQuery,
						data: {
							userPreferences: {
								__typename: 'UserPreferences',
								confluenceEditorSettings: {
									__typename: 'ConfluenceEditorSettings',
									toolbarDockingInitialPosition: value ?? null,
								},
							},
						},
					});
				},
			});

			const userPreferences = parseConfluenceEditorSettings(
				data?.updateUserPreferences?.confluenceEditorSettings,
			);

			updateLocalStorage(userPreferences);

			return { [key]: userPreferences[key] } as Pick<UserPreferences, K>;
		} catch (_error) {
			// In case of an error, we return the key with updated value
			// This is to ensure that the editor can still function without flaws
			// but the value might not be persisted to the server
			return { [key]: value } as Pick<UserPreferences, K>;
		}
	}

	public async loadUserPreferences(): Promise<UserPreferences> {
		let userPreferences = {} as UserPreferences;

		if (!this.isLoggedin) {
			// If the user is not logged in, we return an empty object
			return Promise.resolve(userPreferences);
		}

		try {
			// first time we load we use cache-first policy to avoid unnecessary network requests
			// after the first load we always use no-cache to ensure we have the latest preferences
			// this is particularly important when user has multiple tabs open and changes preferences in one tab
			// we want to ensure other tabs have the latest preferences
			const fetchPolicy =
				!this.isInitialLoad && expVal('cc_editor_preferences_query_optimisation', 'isEnabled', true)
					? 'cache-first'
					: 'no-cache';
			this.isInitialLoad = true;

			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const { data } = await getApolloClient()?.query<EditorPreferencesQueryType>({
				query: EditorPreferencesQuery,
				fetchPolicy,
			});

			userPreferences = parseConfluenceEditorSettings(
				data?.userPreferences?.confluenceEditorSettings,
			);

			updateLocalStorage(userPreferences);
			return userPreferences;
		} catch (_error) {
			// if upload local storage fails, we will still return the user preferences
			return userPreferences;
		}
	}
}

export const getUserPreferencesProvider = (options: {
	isLivePage: boolean;
	persistenceAPI?: UserPreferencesPersistenceAPI;
}) => {
	const persistenceAPI = options.persistenceAPI || new UserPreferencesPersistenceAPI();

	return new UserPreferencesProvider(persistenceAPI, {
		toolbarDockingPosition: options.isLivePage ? 'none' : 'top',
	});
};
